<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-md bg-gray-800 text-gray-300 rounded-lg p-2 sm:p-5 flex flex-col gap-4 relative shadow-lg border border-gray-700 animate-scaleIn">
      <!-- Header -->
      <div class="flex justify-between items-center">
        <h3 class="text-lg font-semibold text-gray-100 flex items-center gap-2">
          <i class="fas fa-explosion text-indigo-400 text-xs"></i>
          ปลดกัปดัก
        </h3>
        <button @click="$emit('close')" class="text-gray-300 hover:text-indigo-300 group">
          <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
        </button>
      </div>

      <!-- Main Content -->
      <div class="overflow-y-auto scrollbar-thin" id="style-1">
        <!-- Loading -->
        <div v-if="isLoading" class="flex flex-col items-center py-8">
          <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-red-600"></div>
          <p class="text-gray-300 text-sm mt-4">กำลังโหลดข้อมูล...</p>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="flex flex-col gap-4">
          <div class="text-center text-red-300 py-6 text-sm bg-gray-800 border border-gray-600 rounded-md">
            {{ error }}
          </div>
          <button
            @click="$emit('close')"
            class="bg-gray-700 hover:bg-gray-600 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 font-semibold border border-gray-600 flex items-center justify-center gap-2"
          >
            <i class="fas fa-times text-xs"></i> ปิด
          </button>
        </div>

        <!-- Result -->
        <div v-else-if="showResult" class="flex flex-col gap-4">
          <div class="p-2 bg-gray-800 border rounded-md transition-all duration-200 border-l-4 border-blue-400">
            <div class="flex items-center gap-2 text-blue-400 text-xs sm:text-sm">
              <i class="fas fa-check"></i>
              <span class="text-gray-300">ใช้ {{ item?.name || 'ไอเทม' }} เพื่อพยายามปลดกัปดักที่ห้อง {{ selectedRooms.join(' ') }}</span>
            </div>
          </div>
          <div class="text-center text-gray-300 text-xs sm:text-sm">
            จำนวนครั้งที่ปลดกัปดักสำหรับเป้าหมายนี้: {{ disarmCount }}/2
          </div>
          <button
            @click="$emit('close')"
            class="bg-gray-700 hover:bg-gray-600 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 font-semibold border border-gray-600 flex items-center justify-center gap-2 w-full"
          >
            <i class="fas fa-times text-xs"></i> ปิด
          </button>
        </div>

        <!-- Content -->
        <div v-else class="flex flex-col gap-4">
          <!-- Opponent -->
          <div v-if="hasTarget" class="bg-gray-800 rounded-md p-1.5 flex items-center border border-gray-700 transition-all duration-200 hover:border-indigo-600 hover:bg-gray-900 cursor-pointer gap-2">
            <img
              :src="displayImage"
              alt="Opponent"
              class="w-9 h-9 rounded-md object-cover border-2 border-gray-600"
            />
            <div class="flex flex-col">
              <h4 class="text-gray-300 text-xs sm:text-sm font-semibold">{{ displayName }}</h4>
              <p class="text-gray-300 text-xs">จำนวนครั้งที่ปลดกัปดัก: {{ disarmCount }}/2</p>
            </div>
          </div>
          <div v-else class="text-yellow-400 text-center text-xs sm:text-sm">
            <i class="fas fa-exclamation-triangle mr-2 text-yellow-400"></i> ต้องค้นหาผู้รอดชีวิตในเมนูก่อนใช้งาน
          </div>

          <!-- Eliminated Rooms Warning -->
          <div v-if="hasEliminatedRooms" class="text-yellow-400 text-center text-xs sm:text-sm">
            <i class="fas fa-exclamation-triangle mr-2 text-yellow-400"></i> ไม่สามารถปลดกัปดักได้ขณะใช้งานวิญญาณค้นหาผู้เล่น
          </div>

          <!-- Item -->
          <div class="bg-gray-800 rounded-md p-1.5 flex items-center border border-gray-700 transition-all duration-200 hover:border-indigo-600 hover:bg-gray-900 cursor-pointer gap-2">
            <img
              :src="item?.img || defaultItemImg"
              alt="Item"
              class="w-9 h-9 rounded-md object-cover border-2 border-gray-600"
              @error="handleImageError"
            />
            <div class="flex flex-col">
              <h4 class="text-gray-300 text-xs sm:text-sm font-semibold truncate">{{ item?.name || 'Unknown' }}</h4>
              <p class="text-gray-300 text-xs">จำนวนห้องที่สำรวจ: {{ item?.disarmRoomCount || 'N/A' }}</p>
            </div>
          </div>

          <!-- Rooms -->
          <div v-if="hasTarget && !hasEliminatedRooms">
            <p class="text-gray-300 text-xs sm:text-sm text-center mb-2">เลือกห้องที่ต้องการสำรวจและปลดกัปดัก (สามารถเลือกได้สูงสุด {{ maxSelections }} ห้อง)</p>
            <div class="grid grid-cols-5 gap-1 sm:gap-1.5">
              <button
                v-for="roomNum in roomCount"
                :key="roomNum"
                @click="selectRoom(roomNum)"
                class="bg-gray-800 p-1 rounded-md border border-gray-700 hover:border-indigo-600 hover:bg-gray-900 transition-all duration-200 flex flex-col items-center justify-center"
                :class="{ 'border-2 border-indigo-600': selectedRooms.includes(roomNum) }"
                :disabled="isDisarming || disarmCount >= 2"
              >
                <i class="fas fa-door-closed text-gray-300 text-xs"></i>
                <span class="text-xs text-gray-300">{{ roomNum }}</span>
              </button>
            </div>
          </div>

          <!-- Warning -->
          <div class="text-yellow-400 text-xs sm:text-sm text-center">
            <i class="fas fa-exclamation-triangle mr-2 text-yellow-400"></i>
            การปลดกัปดักจะลบไอเทมนี้ทันทีและไม่สามารถย้อนกลับได้
          </div>

          <!-- Progress Bar -->
          <div v-if="isDisarming" class="flex flex-col gap-3 mt-2 animate-fadeIn">
            <p class="text-center text-gray-300 text-xs sm:text-sm">กำลังวิ่งไปค้นหากัปดัก...</p>
            <div class="progress-container border border-gray-700 bg-gray-800 rounded-md">
              <div class="progress-bar" :class="progressClass"></div>
              <div class="runner" :class="progressClass"><span>🐭</span></div>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex flex-col gap-2">
            <button
              @click="startDisarm"
              class="bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 border border-indigo-700"
              :disabled="isDisarming || !hasTarget || selectedRooms.length === 0 || disarmCount >= 2 || hasEliminatedRooms"
            >
              <span v-if="!isDisarming"><i class="fas fa-explosion text-xs"></i> ยืนยันปลดกัปดัก</span>
              <span v-else class="flex items-center gap-2">
                <div class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
                กำลังปลด...
              </span>
            </button>

            <button
              @click="$emit('close')"
              class="bg-gray-700 hover:bg-gray-600 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 border border-gray-600"
            >
              <i class="fas fa-times text-xs"></i> ยกเลิก
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import defaultItemImg from '@/assets/default-item.png';

export default {
  name: 'DisarmTrapModal',
  props: {
    item: { type: Object, required: true }
  },
  emits: ['close', 'success', 'disarm-success'],
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();
    const isLoading = ref(true);
    const error = ref(null);
    const isDisarming = ref(false);
    const hasTarget = ref(false);
    const hasEliminatedRooms = ref(false);
    const opponent = ref({});
    const characterPublic = ref({});
    const selectedRooms = ref([]);
    const roomCount = ref(0);
    const showResult = ref(false);
    const disarmCount = ref(0);
    const disarmStatus = ref(null);

    const anonymousImage = 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2Fanonymouse.png?alt=media&token=25259b2d-03f1-49fb-b70c-054cd88d2a86';

    const displayName = computed(() => {
      if (!opponent.value || characterPublic.value?.building === 1 || characterPublic.value?.building === 2) return 'บุคคลลึกลับ';
      return opponent.value.characterName || 'บุคคลลึกลับ';
    });

    const displayImage = computed(() => {
      if (!opponent.value || characterPublic.value?.building === 1 || characterPublic.value?.building === 2) return anonymousImage;
      return opponent.value.imageUrl || anonymousImage;
    });

    const maxSelections = computed(() => props.item?.disarmRoomCount || 1);

    const progressClass = computed(() => {
      if (disarmStatus.value === 'success') return 'result-success';
      if (disarmStatus.value === 'fail') return 'result-fail';
      return 'animating';
    });

    const handleImageError = (e) => {
      e.target.src = defaultItemImg;
    };

    const fetchOpponent = async () => {
      try {
        const data = playerStore.characterPrivateData;
        if (data) {
          const assassinateSurvivorUid = data.assassinateSurvivorUid;
          disarmCount.value = data.disarmCount || 0;
          const eliminatedRooms = data.eliminatedRooms || [];
          hasEliminatedRooms.value = eliminatedRooms.length > 0;
          if (assassinateSurvivorUid) {
            hasTarget.value = true;
            const opponentDoc = await projectFirestore.collection('characters').doc(assassinateSurvivorUid).get();
            if (opponentDoc.exists) {
              opponent.value = opponentDoc.data();
              roomCount.value = opponent.value.room || 1;
            } else {
              error.value = 'ไม่พบข้อมูลเป้าหมาย';
              hasTarget.value = false;
            }
          } else {
            hasTarget.value = false;
          }
        } else {
          error.value = 'ไม่พบข้อมูลส่วนตัว';
          hasTarget.value = false;
        }
        // Fetch character public data from store
        characterPublic.value = playerStore.characterData || {};
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูล: ' + err.message;
        hasTarget.value = false;
      } finally {
        isLoading.value = false;
      }
    };

    const selectRoom = (room) => {
      if (disarmCount.value >= 2 || hasEliminatedRooms.value) return;
      if (selectedRooms.value.includes(room)) {
        selectedRooms.value = selectedRooms.value.filter(r => r !== room);
      } else if (selectedRooms.value.length < maxSelections.value) {
        selectedRooms.value.push(room);
      }
    };

    const startDisarm = async () => {
      if (selectedRooms.value.length === 0 || disarmCount.value >= 2 || hasEliminatedRooms.value) return;
      isDisarming.value = true;
      showResult.value = false;
      disarmStatus.value = null;

      try {
        const instanceId = props.item.instanceId || props.item.id;
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'disarmTrap', instanceId, selectedRooms: selectedRooms.value });

        const charPrivateData = playerStore.characterPrivateData;
        if (charPrivateData) {
          disarmCount.value = charPrivateData.disarmCount || 0;
        }

        disarmStatus.value = 'success';
        setTimeout(() => {
          showResult.value = true;
          isDisarming.value = false;
        }, 500);
        emit('disarm-success', instanceId);
        emit('success', instanceId);
      } catch (err) {
        disarmStatus.value = 'fail';
        setTimeout(() => {
          error.value = err?.message || 'เกิดข้อผิดพลาดในการปลดกัปดัก';
          isDisarming.value = false;
        }, 900);
      }
    };

    onMounted(() => {
      if (user.value?.uid) {
        playerStore.init(user.value.uid);
      }
      fetchOpponent();
    });

    return {
      isLoading,
      error,
      isDisarming,
      hasTarget,
      hasEliminatedRooms,
      opponent,
      characterPublic,
      selectedRooms,
      roomCount,
      maxSelections,
      showResult,
      startDisarm,
      selectRoom,
      handleImageError,
      progressClass,
      disarmCount,
      displayName,
      displayImage,
      defaultItemImg
    };
  }
};
</script>

<style scoped>
/* Thin scrollbar */
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

/* Animations */
@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}

@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn {
  animation: fadeIn 0.3s ease-out;
}

/* Progress Bar */
.progress-container {
  position: relative;
  height: 18px;
  border-radius: 10px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  transition: background-color 0.3s ease;
}

.progress-bar.animating {
  background: linear-gradient(90deg, #6366f1, #4f46e5, #3730a3);
  background-size: 200% 100%;
  animation: fill 3s linear forwards, sheen 1s linear infinite;
  width: 0;
}

.progress-bar.result-success {
  background-color: #22c55e;
  width: 100%;
}

.progress-bar.result-fail {
  background-color: #ef4444;
  width: 100%;
  animation: empty 0.9s linear forwards;
}

@keyframes sheen {
  0% { background-position: 200% 0; }
  100% { background-position: 0 0; }
}

@keyframes fill {
  from { width: 0; }
  to { width: 100%; }
}

@keyframes empty {
  from { width: 100%; }
  to { width: 0; }
}

/* Runner */
.runner {
  position: absolute;
  top: -22px;
  left: 0;
  font-size: 1.25rem;
  width: 28px;
  text-align: center;
  z-index: 2;
}

.runner.animating { animation: run 3s linear forwards; }
.runner.result-success { left: calc(100% - 28px); }
.runner.result-fail { animation: run-back 0.9s linear forwards; }

@keyframes run {
  from { left: 0; }
  to { left: calc(100% - 28px); }
}

@keyframes run-back {
  from { left: calc(100% - 28px); }
  to { left: 0; }
}
</style>
